/* eslint-disable */
import apiService from '@/apis/apiService';
import Cookies from 'js-cookie';
import { invalidateRoleCache } from '@/composables/roles';


function normalizeRolesFromResponse(data) {
    const raw = data?.roles || data?.userRoles || data?.authorities || data?.privileges || [];
    if (Array.isArray(raw)) {
        return raw.map(item => (typeof item === 'string' ? item : (item?.authority || item?.name || item?.role))).filter(Boolean);
    }
    if (typeof raw === 'string') {
        try { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) return parsed; }
        catch (e) { return raw.split(/[\s,]+/).map(x => x.trim()).filter(x => x.startsWith('ROLE_')); }
    }
    return [];
}

function applyRolesAfterSchoolSwitch(data) {
    let roles = normalizeRolesFromResponse(data);
    if (!roles.length) {
        try { roles = JSON.parse(Cookies.get('userRoles') || '[]'); } catch (e) { roles = []; }
    }
    if (roles.includes('ROLE_Super_Admin') && !roles.includes('ROLE_All')) roles = roles.concat('ROLE_All');
    if (roles.length) Cookies.set('userRoles', JSON.stringify(Array.from(new Set(roles))));
    invalidateRoleCache();
}

/**
 * Single source of truth for the "switch active school" flow.
 *
 * Previously each sidebar (academics, coding, finance, library, sms, …
 * 12+ in total) inlined the same boilerplate: POST the change endpoint,
 * write the new tokens, set mainschoolname, drop schoolName/Logo,
 * location.reload(). Two problems with that:
 *
 *   1. Drift. As new fields landed on the server response (e.g.
 *      codingOnly + codingSchool from Phase 1c), some call sites
 *      forgot to write them, leaving module gating stale until the
 *      user logged out + back in.
 *   2. Repetition. Twelve copies of the same 15-line block.
 *
 * Centralising means one place to extend when the server response
 * grows another field, and the same correctness for every caller.
 *
 * Pattern for callers:
 *
 *   import { performSchoolSwitch } from '@/composables/schoolSwitch';
 *   async switchSchool(name) {
 *     if (this.switching) return;
 *     this.switching = true;
 *     try { await performSchoolSwitch(name); }
 *     finally { this.switching = false; }
 *   }
 *
 * The helper triggers a full `location.reload()` on success so every
 * mounted component re-evaluates its role checks from a clean slate.
 */
export async function performSchoolSwitch(schoolName) {
    if (!schoolName) return false;
    try {
        const res = await apiService.apiClient.post(
            '/api/index/changactivatedschool',
            { school: schoolName }
        );
        if (res.status < 200 || res.status >= 300) return false;
        const data = res.data || {};

        // ─── Tokens ──────────────────────────────────────────────
        if (data.accessToken)  localStorage.setItem('authToken', data.accessToken);
        if (data.token)        localStorage.setItem('authToken', data.token);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);

        // ─── Active school name ─────────────────────────────────
        localStorage.setItem('mainschoolname', data.mainSchool || schoolName);
        localStorage.setItem('eleaningmainschoolname', data.mainSchool || schoolName);
        applyRolesAfterSchoolSwitch(data);

        // Drop cached header chips so the sidebar repaints with the
        // new school's identity after reload.
        localStorage.removeItem('schoolName');
        localStorage.removeItem('shortDisplayName');
        localStorage.removeItem('schoolLogo');

        // ─── User object — refresh module-gating fields ─────────
        // The dashboard and each sidebar's `canAccess(module)` check
        // reads coding flags off localStorage.user. JwtResponseAfterSchoolChange
        // already returns the new values; we just need to merge them in.
        // Without this, an admin switching from a coding-only school
        // to a regular one (or vice-versa) sees stale gating until
        // next login.
        try {
            const oldUser = JSON.parse(localStorage.getItem('user') || '{}');
            const merged = Object.assign({}, oldUser, data, {
                roles:        normalizeRolesFromResponse(data).length ? normalizeRolesFromResponse(data) : (oldUser.roles || []),
                codingOnly:   !!data.codingOnly,
                codingSchool: !!data.codingSchool,
                mainSchool:   data.mainSchool || schoolName,
            });
            localStorage.setItem('user', JSON.stringify(merged));
        } catch (e) {
            // localStorage.user might not exist (first-load edge case);
            // that's harmless because login.then() will populate it next.
        }

        // ─── Reload ─────────────────────────────────────────────
        // Every mounted component picks up the new state cleanly.
        location.reload();
        return true;
    } catch (e) {
        console.error('School switch failed:', e);
        return false;
    }
}

/**
 * Convenience reader. The same logic used by index.vue's
 * `isCodingOnlySchool()` check. Always reads fresh from localStorage —
 * sidebars / route guards / dashboards can call this without caching.
 */
export function isCodingOnlySchool() {
    try { return !!JSON.parse(localStorage.getItem('user') || '{}').codingOnly; }
    catch (e) { return false; }
}
